import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RangeTypeClient, RangeTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getApiClient = () => new RangeTypeClient(API_BASE_URL, axiosInstance);

export const useRangeTypes = () =>
  useQuery({
    queryKey: ['rangeTypes'],
    queryFn: async () => {
      const response = await getApiClient().searchRangeTypes({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

export const useRangeTypeSearch = () => {
  const search = async (params: any) => {
    const response = await getApiClient().searchRangeTypes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

export const useRangeTypeById = (id: string | undefined) =>
  useQuery({
    queryKey: ['rangeType', id],
    queryFn: async () => {
      const response = await getApiClient().getRangeTypeById(id!);
      return response?.data || null;
    },
    enabled: !!id,
  });

export const useCreateRangeType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RangeTypeDTO) => getApiClient().createRangeType(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rangeTypes'] }),
  });
};

export const useUpdateRangeType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RangeTypeDTO }) =>
      getApiClient().updateRangeType(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rangeTypes'] }),
  });
};

export const useDeleteRangeType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getApiClient().deleteRangeType(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rangeTypes'] }),
  });
};